import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { Loader2 } from "lucide-react";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:ring-offset-2 focus:ring-offset-surface-900 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "liquid-glass-brand hover:brightness-110 active:brightness-95",
        secondary:
          "liquid-glass-subtle text-surface-100 hover:bg-white/[0.10] hover:border-white/20",
        outline:
          "liquid-glass-subtle text-surface-200 hover:text-surface-50 hover:bg-white/[0.08]",
        ghost:
          "text-surface-300 hover:text-surface-50 hover:bg-white/[0.06] hover:backdrop-blur-md",
        danger:
          "bg-red-500/85 text-white border border-red-400/40 backdrop-blur-md shadow-lg shadow-red-600/25 hover:bg-red-500",
        success:
          "bg-emerald-500/85 text-white border border-emerald-400/40 backdrop-blur-md shadow-lg shadow-emerald-600/25 hover:bg-emerald-500",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, isLoading, children, disabled, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
